import { isNotNullOrUndefinedOrEmpty } from '../../utility/string.js';
import { Language } from '../serializable/language.js';

export class DateTimeFormatPreset {
  private constructor(
        public readonly dateFormat: string | null,
        public readonly separator: string | null,
        public readonly timeFormat: string | null,
  ) {
    let formatter: string = '';
    if(isNotNullOrUndefinedOrEmpty(dateFormat)) {
      formatter = dateFormat;
    }
    if(isNotNullOrUndefinedOrEmpty(separator)) {
      formatter += separator;
    }
    if(isNotNullOrUndefinedOrEmpty(timeFormat)) {
      formatter += timeFormat;
    }
    this.formatter = isNotNullOrUndefinedOrEmpty(formatter) ? formatter : null;
  }

  static auto(): DateTimeFormatPreset {
    return new DateTimeFormatPreset(null, null, null);
  }

  static date(dateFormat: string): DateTimeFormatPreset {
    return new DateTimeFormatPreset(dateFormat, null, null);
  }

  static time(timeFormat: string): DateTimeFormatPreset {
    return new DateTimeFormatPreset(null, null, timeFormat);
  }

  static dateTime(dateFormat: string, timeFormat: string, separator?: string): DateTimeFormatPreset {
    return new DateTimeFormatPreset(dateFormat, separator ?? null, timeFormat);
  }

  formatter: string | null;
}

export class LocalizedDateFormatPresets {
  private constructor(
      public readonly formatters: Map<Language, DateTimeFormatPreset>,
  ) {}

  static YYYYMMMDDHHmm(): LocalizedDateFormatPresets {
    return new LocalizedDateFormatPresets(new Map([
      [Language.English, DateTimeFormatPreset.dateTime('MMM DD. YYYY', 'HH:mm', ', ')],
      [Language.Hungarian, DateTimeFormatPreset.dateTime('YYYY.MMM.DD.', 'HH:mm', ', ')],
      [Language.Romanian, DateTimeFormatPreset.dateTime('DD MMM YYYY', 'HH:mm', ', ')],
    ]));
  }

  static YYYYMMMDD(): LocalizedDateFormatPresets {
    return new LocalizedDateFormatPresets(new Map([
      [Language.English, DateTimeFormatPreset.date('MMM DD. YYYY')],
      [Language.Hungarian, DateTimeFormatPreset.date('YYYY.MMM.DD.')],
      [Language.Romanian, DateTimeFormatPreset.date('DD MMM YYYY')],
    ]));
  }

  static YYYYMMDDHHmm(): LocalizedDateFormatPresets {
    return new LocalizedDateFormatPresets(new Map([
      [Language.English, DateTimeFormatPreset.dateTime('MM.DD.YYYY', 'HH:mm', ', ')],
      [Language.Hungarian, DateTimeFormatPreset.dateTime('YYYY.MM.DD.', 'HH:mm', ', ')],
      [Language.Romanian, DateTimeFormatPreset.dateTime('DD MM YYYY', 'HH:mm', ', ')],
    ]));
  }

  static YYYYMMDD(): LocalizedDateFormatPresets {
    return new LocalizedDateFormatPresets(new Map([
      [Language.English, DateTimeFormatPreset.date('MM.DD.YYYY')],
      [Language.Hungarian, DateTimeFormatPreset.date('YYYY.MM.DD.')],
      [Language.Romanian, DateTimeFormatPreset.date('DD MM YYYY')],
    ]));
  }

  static YYYYMMMMDHHmm(): LocalizedDateFormatPresets {
    return new LocalizedDateFormatPresets(new Map([
      [Language.English, DateTimeFormatPreset.dateTime('YYYY MMMM D', 'HH:mm', ', ')],
      [Language.Hungarian, DateTimeFormatPreset.dateTime('YYYY. MMMM D.', 'HH:mm', ', ')],
      [Language.Romanian, DateTimeFormatPreset.dateTime('D MMMM YYYY', 'HH:mm', ', ')],
    ]));
  }

  static YYYYMMMMD(): LocalizedDateFormatPresets {
    return new LocalizedDateFormatPresets(new Map([
      [Language.English, DateTimeFormatPreset.date('YYYY MMMM D')],
      [Language.Hungarian, DateTimeFormatPreset.date('YYYY. MMMM D.')],
      [Language.Romanian, DateTimeFormatPreset.date('D MMMM YYYY')],
    ]));
  }

  static MMMMDHHmm(): LocalizedDateFormatPresets {
    return new LocalizedDateFormatPresets(new Map([
      [Language.English, DateTimeFormatPreset.dateTime('MMMM D', 'HH:mm', ', ')],
      [Language.Hungarian, DateTimeFormatPreset.dateTime('MMMM D.', 'HH:mm', ', ')],
      [Language.Romanian, DateTimeFormatPreset.dateTime('D MMMM', 'HH:mm', ', ')],
    ]));
  }

  static MMMMD(): LocalizedDateFormatPresets {
    return new LocalizedDateFormatPresets(new Map([
      [Language.English, DateTimeFormatPreset.date('MMMM D')],
      [Language.Hungarian, DateTimeFormatPreset.date('MMMM D.')],
      [Language.Romanian, DateTimeFormatPreset.date('D MMMM')],
    ]));
  }

  static MMMDDHHmm(): LocalizedDateFormatPresets {
    return new LocalizedDateFormatPresets(new Map([
      [Language.English, DateTimeFormatPreset.dateTime('MMM DD', 'HH:mm', ', ')],
      [Language.Hungarian, DateTimeFormatPreset.dateTime('MMM.DD.', 'HH:mm', ', ')],
      [Language.Romanian, DateTimeFormatPreset.dateTime('DD MMM', 'HH:mm', ', ')],
    ]));
  }

  static MMMDD(): LocalizedDateFormatPresets {
    return new LocalizedDateFormatPresets(new Map([
      [Language.English, DateTimeFormatPreset.date('MMM DD')],
      [Language.Hungarian, DateTimeFormatPreset.date('MMM.DD.')],
      [Language.Romanian, DateTimeFormatPreset.date('DD MMM')],
    ]));
  }

  static MMDDHHmm(): LocalizedDateFormatPresets {
    return new LocalizedDateFormatPresets(new Map([
      [Language.English, DateTimeFormatPreset.dateTime('MM.DD', 'HH:mm', ', ')],
      [Language.Hungarian, DateTimeFormatPreset.dateTime('MM.DD.', 'HH:mm', ', ')],
      [Language.Romanian, DateTimeFormatPreset.dateTime('DD MM', 'HH:mm', ', ')],
    ]));
  }

  static MMDD(): LocalizedDateFormatPresets {
    return new LocalizedDateFormatPresets(new Map([
      [Language.English, DateTimeFormatPreset.date('MM.DD')],
      [Language.Hungarian, DateTimeFormatPreset.date('MM.DD.')],
      [Language.Romanian, DateTimeFormatPreset.date('DD MM')],
    ]));
  }

  static DDHHmm(): LocalizedDateFormatPresets {
    return new LocalizedDateFormatPresets(new Map([
      [Language.English, DateTimeFormatPreset.dateTime('DD', 'HH:mm', ', ')],
      [Language.Hungarian, DateTimeFormatPreset.dateTime('DD.', 'HH:mm', ', ')],
      [Language.Romanian, DateTimeFormatPreset.dateTime('DD', 'HH:mm', ', ')],
    ]));
  }

  static DD(): LocalizedDateFormatPresets {
    return new LocalizedDateFormatPresets(new Map([
      [Language.English, DateTimeFormatPreset.date('DD')],
      [Language.Hungarian, DateTimeFormatPreset.date('DD.')],
      [Language.Romanian, DateTimeFormatPreset.date('DD')],
    ]));
  }

  static HHmm(): LocalizedDateFormatPresets {
    return new LocalizedDateFormatPresets(new Map([
      [Language.English, DateTimeFormatPreset.time('HH:mm')],
      [Language.Hungarian, DateTimeFormatPreset.time('HH:mm')],
      [Language.Romanian, DateTimeFormatPreset.time('HH:mm')],
    ]));
  }
}
